'use client';
import AffirmCTA from '@components/AffirmCTA';
import useTranslation from '@components/Translation/useTranslation';
import React, { useState } from 'react';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import Rating from '@components/shared/Bazaarvoice/Rating';
import Breadcrumb from '@repo/ui/src/components/Breadcrumb';
import ProductPrice from '@components/Product/Price';
import BaseAccordion from '@components/BaseAccordion';
import useProductDetail from '@hooks/useProductDetail';
import AlternateSizes from '@components/Product/_AlternateSizes';
import { ProductDetailProps } from '@typings/product';
import ProductImages from '@components/Product/_ProductImages';
import AlternateColors from '@components/Product/_AlternateColors';
import CallToActionButton from '@components/Product/CallToActionButton';
import Seo from '@repo/ui/src/components/Seo';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { ProductProvider } from '@context/ProductProvider';
import { classNames } from '@repo/ui/src/utils/classNames';

const ApparelPage = (props: ProductDetailProps) => {
  const {
    seo,
    title,
    description,
    color,
    productAlertMessage,
    showBreadcrumb,
    breadcrumb,
    alternateSizes,
    alternateColorsV2,
    prop65,
    ownerManual,
    links,
    addOns,
    sizes,
  } = props;

  const {
    photos,
    product,
    photoQuantity,
    isLoading: isProductDataLoading,
    addToCart,
    quantity,
    updateQuantity,
  } = useProductDetail(props);
  const { t } = useTranslation();
  const { currency, eCommEnabled, locale } = useCurrentUrl();
  const [isLoadingAlt, setIsLoadingAlt] = useState(false);
  const isLoading = isProductDataLoading || isLoadingAlt;

  const ownerManualLink =
    ownerManual && ownerManual.ownerManualLink && ownerManual.ownerManualLink.length > 0
      ? buildLinkObj(ownerManual.ownerManualLink[0])
      : buildLinkObj({ displayName: '', link: '' });

  if (!product) return null;

  return (
    <ProductProvider
      product={product}
      altProduct={sizes?.[0]}
      addOns={addOns as any}
      locale={locale}
    >
      <div>
        {showBreadcrumb && <Breadcrumb {...breadcrumb} seoObj={seo?.[0]} />}
        <div>
          <div className="md:tw-grid md:tw-grid-cols-2 md:tw-py-20 tw-max-w-footer tw-m-auto">
            <Seo
              {...seo?.[0]}
              contentRows={['OLD APPAREL PAGE']}
              jsonLD={{ type: 'product', product }}
            />
            <div className="tw-mt-0 tw-p-0">
              <div className="tw-w-full sm:tw-w-[85%]">
                <ProductImages photos={photos} quantity={photoQuantity} />
              </div>
            </div>
            <div className="tw-px-5 ">
              <div>
                <p>{product.sku}</p>
              </div>
              <h1 className="tw-text-6xl tw-max-w-[400px] tw-leading-[60px]">{title}</h1>
              <ProductPrice product={product} className="tw-py-4" align="left" />
              {alternateSizes && (
                <p>
                  <strong>
                    {t('productDetailPageContent.selectSizeText', undefined, {
                      default: 'Select A Size',
                    })}
                  </strong>
                </p>
              )}
              {alternateSizes && (
                <AlternateSizes {...alternateSizes} setIsLoading={setIsLoadingAlt} />
              )}
              <div className="tw-mb-5 tw-mt-4 tw-flex tw-justify-start">
                <Rating sku={product?.sku} enableSEO={false} />
              </div>
              {alternateColorsV2 && <AlternateColors color={color} {...alternateColorsV2} />}

              {productAlertMessage && (
                <p>
                  <strong>{productAlertMessage}</strong>
                </p>
              )}

              {eCommEnabled && !product.ctaButtonType && (
                <input
                  className="tw-mr-2 shadow-none tw-h-12 tw-w-12 tw-rounded tw-border-2 tw-text-center"
                  onChange={(e) => updateQuantity(e)}
                  type="integer"
                  value={quantity}
                  title="Quantity"
                />
              )}
              <CallToActionButton
                buttonProps={{
                  disabled: isLoading,
                  type: 'submit',
                }}
                buttonProcessing={isLoading}
                onClickAddToCart={addToCart}
                product={product}
              />
              {product.price && currency === 'USD' && (
                <AffirmCTA className="tw-mt-3" amount={product.price} />
              )}

              {eCommEnabled && !product.ctaButtonType && (
                <div className="tw-mt-4 tw-mb-5">
                  <a
                    className="tw-border-b-2 tw-border-black"
                    href={
                      buildLinkObj(
                        t('productDetailPageContent.dealerLocatorLink.link', undefined, {
                          default: {
                            link: 'dealers',
                          },
                          returnObjects: true,
                        }),
                      ).href
                    }
                  >
                    {t('productDetailPageContent.shopLocalText', undefined, {
                      default: 'Shop Local',
                    })}
                  </a>
                </div>
              )}
              <hr />
              <p className="tw-my-5">{description}</p>
              {links?.links?.length > 0 &&
                links.links.map((x: any) => {
                  const link = buildLinkObj(x);
                  return link?.href ? (
                    <p key={link.text} className="tw-mb-2">
                      <a href={link.href}>{link.text}</a>
                    </p>
                  ) : null;
                })}
              {ownerManualLink.text && (
                <p className="tw-mt-6">
                  <a className="tw-border-b-2 tw-border-black" href={ownerManualLink.href}>
                    {ownerManualLink.text}
                  </a>
                </p>
              )}
              <hr className={classNames(!prop65 && 'tw-mb-2')} />
              {prop65 && (
                <>
                  <BaseAccordion titleVariant="body1" title={prop65.title}>
                    <p>{prop65.warningBody}</p>
                  </BaseAccordion>
                  <hr />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </ProductProvider>
  );
};

export default ApparelPage;
